import type { DepartmentNode } from "@/entities/org-department/api/orgService";

/** 部门树节点(在 DepartmentNode 上挂 children)。 */
export interface DeptTreeNode extends DepartmentNode {
  children: DeptTreeNode[];
}

/**
 * 把平铺 DepartmentNode[] 建成树:按 parentId 挂子(parentId=0 或父不在集合 → 根);
 * 同级按 sort 再按 name 排。物化 path 天然保证父在子前,但这里用 id map 不依赖顺序。
 */
export function buildDeptTree(nodes: DepartmentNode[]): DeptTreeNode[] {
  const byId = new Map<number, DeptTreeNode>();
  for (const n of nodes) {
    byId.set(n.id, { ...n, children: [] });
  }
  const roots: DeptTreeNode[] = [];
  for (const n of byId.values()) {
    const parent = n.parentId ? byId.get(n.parentId) : undefined;
    if (parent) {
      parent.children.push(n);
    } else {
      roots.push(n);
    }
  }
  const sortRec = (list: DeptTreeNode[]) => {
    list.sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));
    list.forEach((c) => sortRec(c.children));
  };
  sortRec(roots);
  return roots;
}

/**
 * 搜索:返回名字含 query(不分大小写)的部门平铺列表(带祖先路径 label 便于消歧)。
 * query 为空返 [](调用方走树视图)。
 */
export function searchDepartments(
  nodes: DepartmentNode[],
  query: string
): { id: number; label: string }[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const ancestorPath = (n: DepartmentNode): string => {
    const parts: string[] = [n.name];
    let cur = n.parentId ? byId.get(n.parentId) : undefined;
    let guard = 0;
    while (cur && guard++ < 50) {
      parts.unshift(cur.name);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    return parts.join(" / ");
  };
  return nodes
    .filter((n) => n.name.toLowerCase().includes(q))
    .sort((a, b) => a.path.localeCompare(b.path))
    .map((n) => ({ id: n.id, label: ancestorPath(n) }));
}

/** 成员搜索命中行:dept+member 二元组(selectMember 契约)+ 展示 label。 */
export interface MemberMatch {
  principalId: number;
  departmentId: number;
  displayName: string;
  /** 归属部门名(消歧:同名成员/同人多归属各一行)。 */
  departmentName: string;
}

/**
 * 2026-07-13 组织筛选人名搜索:按 displayName 匹配(不分大小写),带归属部门名消歧。
 * 同人多归属=多行(点哪行选哪个部门下的他);部门已被删的行后端已过滤。query 空返 []。
 */
export function searchMembers(
  members: ReadonlyArray<{ principalId: number; displayName: string; departmentId: number }>,
  depts: DepartmentNode[],
  query: string
): MemberMatch[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const deptName = new Map(depts.map((d) => [d.id, d.name]));
  return members
    .filter((m) => m.displayName.toLowerCase().includes(q))
    .map((m) => ({
      principalId: m.principalId,
      departmentId: m.departmentId,
      displayName: m.displayName,
      departmentName: deptName.get(m.departmentId) ?? `#${m.departmentId}`,
    }))
    .sort(
      (a, b) =>
        a.displayName.localeCompare(b.displayName) ||
        a.departmentName.localeCompare(b.departmentName)
    );
}
